import { Directive, HostListener, input, output } from '@angular/core';
import { debounceTime, filter, Subject, throttleTime } from 'rxjs';

@Directive({
  selector: '[appScrolledToBottom]',
})
export class ScrolledToBottomDirective {
  /**
   * Scroll event is fired too often, so we throttle it to reduce performance issues.
   * Too low value can cause performance issues, too high value can cause missed events.
   */
  scrollEventThrottleTime = input(50);
  /**
   * Debounce time for the output event to avoid firing it too often.
   * Too low value means events will be fired too often (not desired when calling requests).
   * Too high value means the user has to wait too long before the event is fired.
   */
  outputDebounceTime = input(300);
  /**
   * Threshold in pixels to consider the scroll as "scrolled to bottom".
   */
  scrollThreshold = input(10);

  scrolledToBottom = output<Event>();

  lastScrollTop = 0;
  scrolled$ = new Subject<Event>();

  constructor() {
    this.handleScrollEvents();
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: Event): void {
    this.scrolled$.next(event);
  }

  handleScrollEvents(): void {
    this.scrolled$
      .pipe(
        throttleTime(this.scrollEventThrottleTime(), null, { leading: false, trailing: true }),
        filter((event) => {
          if (!event.target) {
            console.warn('Scroll event target is not defined in ScrolledToBottomDirective.');
            return;
          }
          const target = event.target as HTMLElement;

          const isScrolledToBottom =
            target.scrollHeight - target.scrollTop <= target.clientHeight + this.scrollThreshold();

          if (isScrolledToBottom && this.lastScrollTop < target.scrollTop) {
            this.lastScrollTop = target.scrollTop;
            return true;
          }

          this.lastScrollTop = target.scrollTop;
          return false;
        }),
        debounceTime(this.outputDebounceTime()),
      )
      .subscribe((event: Event) => {
        this.scrolledToBottom.emit(event);
      });
  }
}
